import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { api } from "store/queries";

interface IProviderOrPayerOption {
  name?: string;
  value?: string | number | null;
}

interface ICaseRateRulesetLike {
  id: string;
  groupName?: string;
  cptCodes?: string[];
}

export interface ICaseRateCptConflict {
  cptCode: string;
  rulesetId: string;
  rulesetName: string;
}

export interface ICaseRateCptConflictsResult {
  conflictsByCombo: Map<string, ICaseRateCptConflict[]>;
  conflictingComboCount: number;
  totalCombos: number;
  hasConflicts: boolean;
  isChecking: boolean;
}

interface IUseCaseRateCptConflictsArgs {
  excludeRulesetId?: string;
}

interface ICombo {
  providerId: string;
  payerId: number;
}

function normalizeToArray(field: unknown): IProviderOrPayerOption[] {
  if (!field) return [];
  if (Array.isArray(field)) {
    return field.filter(
      (item) =>
        item &&
        typeof item === "object" &&
        item.value !== undefined &&
        item.value !== null &&
        item.value !== ""
    );
  }
  if (
    typeof field === "object" &&
    (field as IProviderOrPayerOption).value !== undefined &&
    (field as IProviderOrPayerOption).value !== null &&
    (field as IProviderOrPayerOption).value !== ""
  ) {
    return [field as IProviderOrPayerOption];
  }
  return [];
}

function findConflictsInRulesets(
  rulesets: ICaseRateRulesetLike[],
  codes: Set<string>,
  excludeRulesetId: string | undefined
): ICaseRateCptConflict[] {
  return rulesets
    .filter((ruleset) => !(excludeRulesetId && ruleset.id === excludeRulesetId))
    .flatMap((ruleset) =>
      (ruleset.cptCodes ?? [])
        .filter((code) => codes.has(code))
        .map((code) => ({
          cptCode: code,
          rulesetId: ruleset.id,
          rulesetName: ruleset.groupName || ruleset.id,
        }))
    );
}

export function useCaseRateCptConflicts({
  excludeRulesetId,
}: IUseCaseRateCptConflictsArgs = {}): ICaseRateCptConflictsResult {
  const dispatch = useDispatch();
  const { watch } = useFormContext();
  const provider = watch("provider");
  const payerName = watch("payerName");
  const cptCodes: string[] = watch("cptCodes") || [];
  const isDefault = !!watch("isDefault");

  const combos = useMemo<ICombo[]>(() => {
    const providers = normalizeToArray(provider);
    const payers = normalizeToArray(payerName);
    return providers.flatMap((p) =>
      payers
        .map((pa) => ({
          providerId: String(p.value),
          payerId: Number(pa.value),
        }))
        .filter((c) => c.providerId && !Number.isNaN(c.payerId))
    );
  }, [provider, payerName]);

  const comboKey = combos.map((c) => `${c.providerId}:${c.payerId}`).join("|");
  const codesKey = [...cptCodes].sort().join(",");

  const [state, setState] = useState<{
    conflictsByCombo: Map<string, ICaseRateCptConflict[]>;
    isChecking: boolean;
  }>({ conflictsByCombo: new Map(), isChecking: false });

  useEffect(() => {
    if (isDefault || combos.length === 0 || cptCodes.length === 0) {
      setState({ conflictsByCombo: new Map(), isChecking: false });
      return undefined;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, isChecking: true }));

    const subscriptions = combos.map((combo) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (dispatch as any)(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (api.endpoints as any).getCaseRateRulesets.initiate({
          isDefault: false,
          providerId: combo.providerId,
          payerId: combo.payerId,
        })
      )
    );

    Promise.all(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      subscriptions.map((sub: any, idx) =>
        sub
          .unwrap()
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .then((res: any) => ({
            combo: combos[idx],
            rulesets: (res?.data ?? []) as ICaseRateRulesetLike[],
          }))
          .catch(() => ({
            combo: combos[idx],
            rulesets: [] as ICaseRateRulesetLike[],
          }))
      )
    ).then((results) => {
      if (cancelled) return;
      const codes = new Set(cptCodes);
      const next = new Map<string, ICaseRateCptConflict[]>();
      results.forEach(({ combo, rulesets }) => {
        const conflicts = findConflictsInRulesets(
          rulesets,
          codes,
          excludeRulesetId
        );
        if (conflicts.length > 0) {
          next.set(`${combo.providerId}:${combo.payerId}`, conflicts);
        }
      });
      setState({ conflictsByCombo: next, isChecking: false });
    });

    return () => {
      cancelled = true;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      subscriptions.forEach((sub: any) => sub?.unsubscribe?.());
    };
  }, [comboKey, codesKey, excludeRulesetId, isDefault, dispatch]);

  return {
    conflictsByCombo: state.conflictsByCombo,
    conflictingComboCount: state.conflictsByCombo.size,
    totalCombos: combos.length,
    hasConflicts: state.conflictsByCombo.size > 0,
    isChecking: state.isChecking,
  };
}
